import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatMenuTrigger } from '@angular/material/menu';
import type { Chart, ChartConfiguration } from 'chart.js';
import { ClientService } from 'src/app/client/client.service';
import { CLIENT_DIALOG_CONFIG, CreateComponent } from 'src/app/client/create/create.component';
import { PrintCellComponent } from 'src/app/computer/print-cell/print-cell.component';
import { ExportService } from 'src/app/docs/export/export.service';
import { AuthService } from 'src/app/login/auth.service';
import {
  MoneyDayRefundReport,
  MoneyDayRefundReportService
} from 'src/app/money-flow/money-day-refund-report.service';
import { MoneyDayService } from 'src/app/money-flow/money-day.service';
import { AppState } from 'src/app/redux/app.state';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { SupportService } from 'src/app/support.service';
import { UploadComponent } from 'src/app/provider-price/upload/upload.component';
import { toLocalDateString } from 'src/app/shared/utils/date-filter.util';
import { parseVinCandidate, VIN_LENGTH, VinCandidate } from 'src/app/shared/utils/vin.util';
import {
  B2cPresenceResponse,
  SearchHistoryEntry,
  SearchHistoryKind,
  CurrencyTrendRate,
  CurrencyTrendResponse,
  CurrentMoneyDaySummary,
  MoneyCashboxChangedEvent,
  MoneyFlowChangedEvent,
  MoneyDay,
  OilHistoryResponse,
  ProductWithControls,
  RingostatAvailabilityStatus,
  RingostatUserSettings,
  User
} from 'src/typings';
import {
  DeviceTypeEnum,
  MoneyDayCollectionStatusEnum,
  MoneyDayStatusEnum,
  MoneyTypeEnum
} from 'src/enums';
import { ShellNavItem } from '../shell-nav.config';
import { HeaderCurrencyService } from './header-currency.service';
import { RingostatUserSettingsService } from 'src/app/user/ringostat-user-settings.service';
import { RingostatCallService } from 'src/app/ringostat/ringostat-call.service';
import { ProductCodeSearchDialogComponent } from './product-code-search-dialog.component';
import { SearchHistoryService } from 'src/app/services/search-history.service';

interface HeaderMoneyTypeRow {
  moneyType: MoneyTypeEnum;
  label: MoneyTypeEnum;
  value: number;
}

/**
 * Результат предпоиска (режим v2): что распознано в строке поиска —
 * клиент по телефону. label/hint — для бейджа,
 * route — куда вести по клику/Enter.
 */
interface SearchPreview {
  type: 'client';
  label: string;
  hint?: string;
  route: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() navigationLinks: ShellNavItem[] = [];

  @Input() orderTooltip = '';

  @Output() toggleMenuEvent: EventEmitter<void> = new EventEmitter();

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement> | undefined;

  @ViewChild('oilHistoryChartCanvas') oilHistoryChartCanvas:
    | ElementRef<HTMLCanvasElement>
    | undefined;

  img = 'assets/logo.svg';

  readonly searchPlaceholder = 'Телефон +380..., товар / артикул или VIN';

  readonly vinLength = VIN_LENGTH;

  readonly summaryMoneyTypes: MoneyTypeEnum[] = [
    MoneyTypeEnum.nal,
    MoneyTypeEnum.terminal,
    MoneyTypeEnum.card,
    MoneyTypeEnum.bn,
    MoneyTypeEnum.A
  ];

  deviceType: DeviceTypeEnum = DeviceTypeEnum.DESKTOP;

  mobileHeaderOpen = false;

  user: User | undefined;

  cartProducts: ProductWithControls[] = [];

  searchControl = new FormControl<string>('');

  /** Активный режим поиска товара. v1 — текущая логика, v2 — новая (обкатываем параллельно). */
  searchMode: 'v1' | 'v2' = 'v1';

  private readonly searchModeStorageKey = 'search.mode';

  /** Клиент, распознанный по телефону в режиме v2. */
  searchPreview: SearchPreview | null = null;

  /** Глобальная история запросов (общая для всех менеджеров). */
  searchHistoryEntries: SearchHistoryEntry[] = [];

  private readonly searchHistoryFocus$ = new Subject<void>();

  private searchHistorySubscription?: Subscription;

  private ringostatAttentionSubscription?: Subscription;

  private ringostatFinishedSubscription?: Subscription;

  private ringostatAttentionTimerId: ReturnType<typeof setInterval> | null = null;

  @ViewChild('searchInput', { read: MatAutocompleteTrigger })
  private searchHistoryTrigger?: MatAutocompleteTrigger;

  @ViewChild('dailySummaryTrigger')
  private dailySummaryTrigger?: MatMenuTrigger;

  private searchPreviewSubscription?: Subscription;

  private previewRefreshSubscription?: Subscription;

  exportDocGW: Subscription;

  moneyCashboxGW: Subscription;

  moneyLifecycleGW: Subscription;

  moneyFlowGW: Subscription;

  moneyLocalFlowGW: Subscription;

  storeSubscription: Subscription;

  routeSyncSubscription: Subscription;

  private _shiftRefundReports: Record<number, MoneyDayRefundReport> = {};

  shiftRefundReportsLoading = false;

  shiftRefundReportsError: string | null = null;

  private _currentMoneyDaySummary: CurrentMoneyDaySummary | null = null;

  currentMoneyDayLoading = false;

  currentMoneyDayError: string | null = null;

  private _managerMoneyDays: MoneyDay[] = [];

  managerMoneyDaysLoading = false;

  managerMoneyDaysError: string | null = null;

  private _selectedManagerMoneyDayId: number | null = null;

  summaryRefreshTimerId: ReturnType<typeof setInterval> | null = null;

  currencyRefreshTimerId: ReturnType<typeof setInterval> | null = null;

  b2cPresenceRefreshTimerId: ReturnType<typeof setInterval> | null = null;

  lastSummaryLoadedAt: Date | null = null;

  currencyTrend: CurrencyTrendResponse | null = null;

  currencyTrendLoading = false;

  currencyTrendError: string | null = null;

  oilHistory: OilHistoryResponse | null = null;

  oilHistoryLoading = false;

  oilHistoryError: string | null = null;

  oilHistoryAttempted = false;

  b2cPresence: B2cPresenceResponse | null = null;

  b2cPresenceLoading = false;

  b2cPresenceError: string | null = null;

  selectedOilHistoryDays: 30 | 90 | 365 = 30;

  ringostatAvailabilityStatus: RingostatAvailabilityStatus = 'online';

  ringostatDndUntil: string | Date | null = null;

  ringostatAvailabilityLoading = false;

  ringostatAvailabilitySaving = false;

  readonly ringostatDndMinuteOptions = [15, 30, 60, 120];

  private lastSummaryUserId?: number;

  private moneyFlowViewStateDirty = true;

  private openManagerMoneyDaysCache: MoneyDay[] = [];

  private pendingManagerMoneyDaysCache: MoneyDay[] = [];

  private popupVisibleManagerMoneyDaysCache: MoneyDay[] = [];

  private managerMoneyDaysForPopupCache: MoneyDay[] = [];

  private selectedManagerMoneyDayCache: MoneyDay | null = null;

  private isManagerMoneyDaysSummarySelectedCache = false;

  private cashShiftSelectionErrorCache: string | null = null;

  private managerMoneyDaysSummaryStatusLabelCache = this.formatShiftCount(0);

  private managerMoneyDaysSummaryCashAmountCache = 0;

  private managerMoneyDaysSummaryTypeRowsCache: HeaderMoneyTypeRow[] = [];

  private selectedManagerMoneyDayTypeRowsCache: HeaderMoneyTypeRow[] = [];

  private selectedManagerMoneyDayCashAmountCache = 0;

  private selectedManagerMoneyDayExpenseAmountCache = 0;

  closingManagerMoneyDayId: number | null = null;

  private currentShiftRefundReportCache: MoneyDayRefundReport = this.createEmptyRefundReport(null);

  private returnsByTypeRowsCache: HeaderMoneyTypeRow[] = [];

  private currentShiftRefundScopeLabelCache = 'Смена не выбрана';

  private headerMoneyTotalCache = 0;

  private readonly lastCashboxLedgerEntryIds = new Map<number, number>();

  private oilHistoryChart: Chart | null = null;

  private chartJsLoader: Promise<typeof import('chart.js')> | null = null;

  private readonly clearSearchListener = () => {
    this.clearSearch();
  };

  constructor(
    private authService: AuthService,
    public supportService: SupportService,
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private exportService: ExportService,
    private clientService: ClientService,
    private dialog: MatDialog,
    private router: Router,
    private headerCurrencyService: HeaderCurrencyService,
    private moneyDayService: MoneyDayService,
    private moneyDayRefundReportService: MoneyDayRefundReportService,
    private ringostatSettingsService: RingostatUserSettingsService,
    private ringostatCallService: RingostatCallService,
    private searchHistoryService: SearchHistoryService
  ) {
    this.syncSearchControlWithRoute();
    this.routeSyncSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.mobileHeaderOpen = false;
        this.syncSearchControlWithRoute();
      });
    this.exportDocGW = this.exportService.docGw.subscribe(() => {
      this.refreshMoneyFlowSummary(true);
    });
    this.moneyCashboxGW = (this.moneyDayService.cashboxChanges || of(null))
      .pipe(
        filter(
          (event): event is MoneyCashboxChangedEvent =>
            Boolean(event) && this.isOwnCashboxChange(event as MoneyCashboxChangedEvent)
        ),
        debounceTime(120)
      )
      .subscribe((event) => this.handleCashboxChange(event));
    this.moneyFlowGW = (this.moneyDayService.flowChanges || of(null))
      .pipe(
        filter(
          (event): event is MoneyFlowChangedEvent =>
            Boolean(event) &&
            ['PAYMENT', 'REFUND', 'CORRECTION', 'SERVICE'].includes(
              String((event as MoneyFlowChangedEvent).change_kind || '')
            ) &&
            Number((event as MoneyFlowChangedEvent).manager_user_id || 0) ===
              Number(this.user?.id || 0) &&
            Number((event as MoneyFlowChangedEvent).warehouse_id || 0) ===
              Number(this.user?.defaultWarehouse?.id || 0)
        ),
        debounceTime(120)
      )
      .subscribe(() => this.refreshMoneyFlowSummary(true));
    this.moneyLocalFlowGW = (this.moneyDayService.localFlowChanges || of(null))
      .pipe(debounceTime(80))
      .subscribe(() => this.refreshMoneyFlowSummary(true));
    this.moneyLifecycleGW = (this.moneyDayService.lifecycleChanges || of(null))
      .pipe(
        filter(
          (day): day is MoneyDay =>
            Boolean(day) &&
            Number((day as MoneyDay).manager_user_id || 0) === Number(this.user?.id || 0) &&
            Number((day as MoneyDay).warehouse_id || 0) ===
              Number(this.user?.defaultWarehouse?.id || 0)
        )
      )
      .subscribe((day) => this.handleMoneyDayLifecycleChange(day));
    this.storeSubscription = this.store.subscribe((data) => {
      const nextUserId = data.user.info?.id;
      const userChanged = nextUserId !== this.lastSummaryUserId;

      this.deviceType = data.user.deviceType;
      this.user = data.user.info;
      this.cartProducts = data.cart;

      if (userChanged) {
        this.lastSummaryUserId = nextUserId;
        this.lastCashboxLedgerEntryIds.clear();
        if (nextUserId) {
          this.refreshMoneyFlowSummary(false);
          void this.loadRingostatAvailability(nextUserId);
          this.restartSummaryTimer();
        } else {
          this.shiftRefundReports = {};
          this.shiftRefundReportsLoading = false;
          this.shiftRefundReportsError = null;
          this.currentMoneyDaySummary = null;
          this.currentMoneyDayLoading = false;
          this.currentMoneyDayError = null;
          this.managerMoneyDays = [];
          this.managerMoneyDaysLoading = false;
          this.managerMoneyDaysError = null;
          this.resetRingostatAvailability();
          this.stopSummaryTimer();
        }
      }
    });
    window.addEventListener('clearSearchControl', this.clearSearchListener);
  }

  get shiftRefundReports() {
    return this._shiftRefundReports;
  }

  set shiftRefundReports(value: Record<number, MoneyDayRefundReport>) {
    this._shiftRefundReports = value || {};
    this.markMoneyFlowViewStateDirty();
  }

  get currentMoneyDaySummary() {
    return this._currentMoneyDaySummary;
  }

  set currentMoneyDaySummary(value: CurrentMoneyDaySummary | null) {
    this._currentMoneyDaySummary = value;
    this.markMoneyFlowViewStateDirty();
  }

  get managerMoneyDays() {
    return this._managerMoneyDays;
  }

  set managerMoneyDays(value: MoneyDay[]) {
    this._managerMoneyDays = value || [];
    this.markMoneyFlowViewStateDirty();
  }

  get selectedManagerMoneyDayId() {
    return this._selectedManagerMoneyDayId;
  }

  set selectedManagerMoneyDayId(value: number | null) {
    this._selectedManagerMoneyDayId = Number(value || 0) || null;
    this.markMoneyFlowViewStateDirty();
  }

  get showMenuButton() {
    return this.deviceType === DeviceTypeEnum.TABLET || this.deviceType === DeviceTypeEnum.MOBILE;
  }

  get showTopNavigation() {
    return this.deviceType === DeviceTypeEnum.DESKTOP || this.deviceType === DeviceTypeEnum.LAPTOP;
  }

  get isCompactLayout() {
    return this.deviceType === DeviceTypeEnum.TABLET || this.deviceType === DeviceTypeEnum.MOBILE;
  }

  get cartSummaryTooltip() {
    return `${this.supportService.formatMoney(this.getSumCart())}\n${
      this.cartProducts.length
    } поз.`;
  }

  get dailyReceivedSumLabel() {
    if (this.managerMoneyDaysLoading && !this.managerMoneyDays.length) {
      return '...';
    }

    return this.supportService.formatMoney(this.getHeaderMoneyTotal());
  }

  get shiftChipTitle() {
    return `Касса • ${this.dailyReceivedSumLabel}`;
  }

  get shiftChipTone() {
    if (this.managerMoneyDaysLoading && !this.managerMoneyDays.length) {
      return 'muted';
    }
    if (this.cashShiftSelectionError) {
      return 'warning';
    }
    if (this.openManagerMoneyDays.length > 0) {
      return 'info';
    }
    if (this.hasPendingManagerCollections()) {
      return 'warning';
    }
    if (this.managerMoneyDaysForPopup.length) {
      return 'neutral';
    }

    return 'muted';
  }

  get shiftChipClass() {
    return `shiftChip shiftChip--${this.shiftChipTone}`;
  }

  get isAdmin() {
    return Boolean(this.user?.roles?.some((role) => role.description === 'admin'));
  }

  get effectiveRingostatAvailabilityStatus(): RingostatAvailabilityStatus {
    if (
      this.ringostatAvailabilityStatus === 'dnd_temporary' &&
      !this.isFutureDate(this.ringostatDndUntil)
    ) {
      return 'online';
    }

    return this.ringostatAvailabilityStatus || 'online';
  }

  get ringostatStatusLabel() {
    if (this.effectiveRingostatAvailabilityStatus === 'dnd') {
      return 'Не беспокоить';
    }

    if (this.effectiveRingostatAvailabilityStatus === 'dnd_temporary') {
      const until = this.formatRingostatDndUntil(this.ringostatDndUntil);
      return until ? `Не беспокоить до ${until}` : 'Не беспокоить временно';
    }

    return 'Онлайн';
  }

  get userMenuStatusClass() {
    return `userMenuTrigger__status userMenuTrigger__status--${this.effectiveRingostatAvailabilityStatus}`;
  }

  get userToolsStatusDotClass() {
    return `userToolsStatus__dot userToolsStatus__dot--${this.effectiveRingostatAvailabilityStatus}`;
  }

  get openManagerMoneyDays() {
    this.ensureMoneyFlowViewState();
    return this.openManagerMoneyDaysCache;
  }

  get pendingManagerMoneyDays() {
    this.ensureMoneyFlowViewState();
    return this.pendingManagerMoneyDaysCache;
  }

  get popupVisibleManagerMoneyDays() {
    this.ensureMoneyFlowViewState();
    return this.popupVisibleManagerMoneyDaysCache;
  }

  get managerMoneyDaysForPopup() {
    this.ensureMoneyFlowViewState();
    return this.managerMoneyDaysForPopupCache;
  }

  get selectedManagerMoneyDay() {
    this.ensureMoneyFlowViewState();
    return this.selectedManagerMoneyDayCache;
  }

  get isManagerMoneyDaysSummarySelected() {
    this.ensureMoneyFlowViewState();
    return this.isManagerMoneyDaysSummarySelectedCache;
  }

  get cashShiftSelectionError() {
    this.ensureMoneyFlowViewState();
    return this.cashShiftSelectionErrorCache;
  }

  get managerMoneyDaysSummaryStatusLabel() {
    this.ensureMoneyFlowViewState();
    return this.managerMoneyDaysSummaryStatusLabelCache;
  }

  get managerMoneyDaysSummaryCashAmount() {
    this.ensureMoneyFlowViewState();
    return this.managerMoneyDaysSummaryCashAmountCache;
  }

  get managerMoneyDaysSummaryTypeRows() {
    this.ensureMoneyFlowViewState();
    return this.managerMoneyDaysSummaryTypeRowsCache;
  }

  get selectedManagerMoneyDayTypeRows() {
    this.ensureMoneyFlowViewState();
    return this.selectedManagerMoneyDayTypeRowsCache;
  }

  get selectedManagerMoneyDayCashAmount() {
    this.ensureMoneyFlowViewState();
    return this.selectedManagerMoneyDayCashAmountCache;
  }

  get selectedManagerMoneyDayExpenseAmount() {
    this.ensureMoneyFlowViewState();
    return this.selectedManagerMoneyDayExpenseAmountCache;
  }

  get currentMoneyDayStatusLabel() {
    if (this.managerMoneyDaysLoading && !this.managerMoneyDays.length) {
      return 'Загрузка смен';
    }
    const day = this.selectedManagerMoneyDay;
    if (day?.status === MoneyDayStatusEnum.OPEN) {
      return 'Смена открыта';
    }
    if (this.pendingManagerMoneyDays.length) {
      return 'Ожидает инкассации';
    }
    if (day?.collection_status === MoneyDayCollectionStatusEnum.COLLECTED) {
      return 'Инкассирована';
    }
    if (day?.status === MoneyDayStatusEnum.CLOSED) {
      return 'Смена закрыта';
    }

    return 'Смен нет';
  }

  get currentMoneyDayStatusClass() {
    return `summary-chip summary-chip--${this.shiftChipTone}`;
  }

  get canCollectCurrentMoneyDay() {
    return Boolean(
      this.isAdmin &&
        this.currentMoneyDaySummary?.can_collect &&
        this.currentMoneyDaySummary?.day_id
    );
  }

  get returnsByTypeRows() {
    this.ensureMoneyFlowViewState();
    return this.returnsByTypeRowsCache;
  }

  get currentShiftRefundReport() {
    this.ensureMoneyFlowViewState();
    return this.currentShiftRefundReportCache;
  }

  get currentShiftRefundScopeLabel() {
    this.ensureMoneyFlowViewState();
    return this.currentShiftRefundScopeLabelCache;
  }

  get currencyRatesForHeader() {
    const rates = this.currencyTrend?.rates || [];
    const sortOrder: Record<string, number> = { EUR: 0, USD: 1 };
    return rates
      .filter((rate) => rate.currency === 'EUR' || rate.currency === 'USD')
      .sort((left, right) => sortOrder[left.currency] - sortOrder[right.currency]);
  }

  get oilLatest() {
    return this.oilHistory?.latest || null;
  }

  ngOnInit(): void {
    this.searchMode = localStorage.getItem(this.searchModeStorageKey) === 'v2' ? 'v2' : 'v1';
    this.searchPreviewSubscription = this.searchControl.valueChanges
      .pipe(
        debounceTime(250),
        map((value) => String(value || '')),
        distinctUntilChanged(),
        switchMap((value) => this.resolveSearchPreview(value))
      )
      .subscribe((preview) => {
        this.searchPreview = preview;
      });
    this.searchHistorySubscription = merge(
      this.searchControl.valueChanges.pipe(debounceTime(250)),
      this.searchHistoryFocus$
    )
      .pipe(
        switchMap(() =>
          this.searchHistoryService
            .list(String(this.searchControl.value || ''))
            .pipe(catchError(() => of(null)))
        )
      )
      .subscribe((response) => {
        if (response) {
          this.searchHistoryEntries = response.items || [];
        }
      });
    if (this.user?.id && !this.managerMoneyDays.length) {
      this.refreshMoneyFlowSummary(false);
    }
    this.restartSummaryTimer();
    this.loadCurrencyTrend(false);
    this.loadOilHistory(false);
    this.loadB2cPresence(false);
    this.restartCurrencyTimer();
    this.restartB2cPresenceTimer();
    this.refreshRingostatAttention();
    this.ringostatFinishedSubscription = merge(
      this.ringostatCallService.callFinishedGW,
      this.ringostatCallService.attentionChanged$
    )
      .pipe(debounceTime(750))
      .subscribe(() => this.refreshRingostatAttention());
    this.ringostatAttentionTimerId = setInterval(() => this.refreshRingostatAttention(), 60000);
  }

  ngOnDestroy(): void {
    this.exportDocGW?.unsubscribe();
    this.moneyCashboxGW?.unsubscribe();
    this.moneyLifecycleGW?.unsubscribe();
    this.moneyFlowGW?.unsubscribe();
    this.moneyLocalFlowGW?.unsubscribe();
    this.storeSubscription?.unsubscribe();
    this.routeSyncSubscription?.unsubscribe();
    this.searchPreviewSubscription?.unsubscribe();
    this.previewRefreshSubscription?.unsubscribe();
    this.searchHistorySubscription?.unsubscribe();
    this.ringostatAttentionSubscription?.unsubscribe();
    this.ringostatFinishedSubscription?.unsubscribe();
    if (this.ringostatAttentionTimerId) clearInterval(this.ringostatAttentionTimerId);
    this.stopSummaryTimer();
    this.stopCurrencyTimer();
    this.stopB2cPresenceTimer();
    this.destroyOilHistoryChart();
    window.removeEventListener('clearSearchControl', this.clearSearchListener);
  }

  private refreshRingostatAttention(): void {
    this.ringostatAttentionSubscription?.unsubscribe();
    this.ringostatAttentionSubscription = this.ringostatCallService
      .getAttentionSummary()
      .pipe(catchError(() => of(null)))
      .subscribe({
        next: (summary) => {
          if (!summary) return;
          const callsLink = this.navigationLinks.find((item) => item.path === '/calls');
          if (!callsLink) return;
          callsLink.badge = summary.unreturnedMissedTotal;
          callsLink.secondaryBadge = summary.voicemailTotal;
          callsLink.badgeTooltip = `Без перезвона: ${summary.unreturnedMissedTotal}; голосовая почта: ${summary.voicemailTotal}`;
        }
      });
  }

  @HostListener('document:keydown.alt.l', ['$event'])
  @HostListener('document:keydown.alt.д', ['$event'])
  selectSearch(event: KeyboardEvent) {
    event.preventDefault();
    if (this.isCompactLayout && !this.mobileHeaderOpen) {
      this.mobileHeaderOpen = true;
      // Wait until Angular reveals the search field before moving keyboard focus.
      setTimeout(() => this.focusSearchInput());
    } else {
      this.focusSearchInput();
    }
  }

  private focusSearchInput() {
    this.searchInput?.nativeElement.focus();
    this.searchInput?.nativeElement.select();
  }

  // Match physical shortcuts outside Angular; ordinary typing must not trigger a view check.
  @HostListener('document:keydown.code.alt.keyq', ['$event'])
  @HostListener('document:keydown.code.control.alt.digit1', ['$event'])
  @HostListener('document:keydown.code.control.alt.numpad1', ['$event'])
  @HostListener('document:keydown.code.control.alt.digit2', ['$event'])
  @HostListener('document:keydown.code.control.alt.numpad2', ['$event'])
  @HostListener('document:keydown.code.control.alt.digit3', ['$event'])
  @HostListener('document:keydown.code.control.alt.numpad3', ['$event'])
  @HostListener('document:keydown.code.control.alt.digit4', ['$event'])
  @HostListener('document:keydown.code.control.alt.numpad4', ['$event'])
  @HostListener('document:keydown.code.control.alt.digit5', ['$event'])
  @HostListener('document:keydown.code.control.alt.numpad5', ['$event'])
  @HostListener('document:keydown.code.control.alt.digit6', ['$event'])
  @HostListener('document:keydown.code.control.alt.numpad6', ['$event'])
  handleHeaderNavigationHotkeys(event: KeyboardEvent) {
    if (
      event.code === 'KeyQ' && event.altKey && !event.ctrlKey &&
      !event.shiftKey && !event.metaKey
    ) {
      if (
        event.defaultPrevented || event.repeat || event.isComposing ||
        event.getModifierState?.('AltGraph') || this.dialog.openDialogs.length
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.openProductCodeSearch();
      return;
    }

    if (!event.ctrlKey || !event.altKey || event.shiftKey || event.metaKey) {
      return;
    }

    const targetPathByCode: Record<string, string> = {
      Digit1: '/parts-selection/new',
      Numpad1: '/parts-selection/new',
      Digit2: '/orders/create',
      Numpad2: '/orders/create',
      Digit3: '/docs/export/create',
      Numpad3: '/docs/export/create',
      Digit4: '/docs/buy/receiving',
      Numpad4: '/docs/buy/receiving',
      Digit5: '/docs/document-receiving',
      Numpad5: '/docs/document-receiving',
      Digit6: '/docs/document-receiving/review',
      Numpad6: '/docs/document-receiving/review'
    };
    const targetPath = targetPathByCode[event.code];

    if (!targetPath) {
      return;
    }

    event.preventDefault();
    this.router.navigateByUrl(targetPath);
  }

  clearSearch() {
    this.searchControl.setValue('');
    this.searchPreview = null;
    this.router.navigateByUrl('/new-catalog');
  }

  toggleMenu() {
    this.toggleMenuEvent.emit();
  }

  getSumCart() {
    return this.cartProducts.reduce(
      (sum, product) => sum + Number(product.quan) * Number(product.retail),
      0
    );
  }

  printCell() {
    return this.dialog.open(PrintCellComponent);
  }

  createClient() {
    const dialog = this.dialog.open(CreateComponent, CLIENT_DIALOG_CONFIG);
    const phone = String(this.searchControl.value || '')
      .replace(/\D/g, '')
      .replace('38', '');
    dialog.componentInstance.phoneFormControl.setValue(phone);
    dialog.componentInstance.createClient.subscribe((client) => {
      this.router.navigateByUrl(`/client/profile/${client.id}`);
      dialog.close();
    });
  }

  /**
   * Распознанный в строке поиска VIN: частичный — для бейджа-счётчика,
   * полный (17 символов) — для перехода в каталог Laximo. Работает в обоих режимах поиска.
   */
  get vinCandidate(): VinCandidate | null {
    return parseVinCandidate(String(this.searchControl.value || ''));
  }

  getVinBadgeTooltip(candidate: VinCandidate) {
    return candidate.isComplete
      ? 'Открыть каталог Laximo по VIN'
      : `VIN: набрано ${candidate.length} из ${this.vinLength} символов`;
  }

  openLaximoCatalog(vin: string) {
    this.router.navigate(['/laximo/vin', vin]);
  }

  async search() {
    // Полный VIN в строке — уходим в каталог Laximo, а не в поиск запчастей.
    const vinCandidate = this.vinCandidate;
    if (vinCandidate?.isComplete) {
      this.recordSearchHistory(vinCandidate.vin, 'vin');
      this.openLaximoCatalog(vinCandidate.vin);
      return;
    }
    // В режиме v2 Enter открывает клиента, распознанного по телефону.
    if (this.searchMode === 'v2' && this.searchPreview) {
      this.recordSearchHistory(
        String(this.searchControl.value || ''),
        'phone',
        this.searchPreview.label
      );
      this.openSearchPreview();
      return;
    }
    const searchValue = String(this.searchControl.value || '');
    if (searchValue.match(/^\+/) && searchValue.length === 13) {
      this.recordSearchHistory(searchValue, 'phone');
      const client = await firstValueFrom(
        this.clientService.search({
          phone: searchValue
        })
      );
      if (client.length === 1) {
        this.router.navigateByUrl(`/client/profile/${client.data[0].id}`);
      } else {
        const ref = this.notificationService.warningWithAction('Клиент не найден!', 'Создать');
        ref.onAction().subscribe(() => {
          this.createClient();
        });
      }
    } else {
      const query = searchValue.replace(/\//g, '').trim();
      this.recordSearchHistory(query, 'text');
      this.router.navigate(['/new-catalog'], {
        queryParams: {
          query: query || null,
          searchMode: this.searchMode === 'v2' ? 'v2' : null
        }
      });
    }
    this.searchInput?.nativeElement.focus();
    this.searchInput?.nativeElement.select();
    // Панель истории не нужна поверх результатов только что запущенного поиска.
    setTimeout(() => this.searchHistoryTrigger?.closePanel());
  }

  /**
   * Переключает режим поиска (v1/v2) и сохраняет выбор.
   * Сразу пересчитывает предпоиск для текущего ввода (в v2) или сбрасывает его (в v1).
   */
  setSearchMode(mode: 'v1' | 'v2'): void {
    if (this.searchMode === mode) {
      return;
    }
    this.searchMode = mode;
    localStorage.setItem(this.searchModeStorageKey, mode);
    this.refreshSearchPreview();
  }

  openProductCodeSearch(): void {
    this.searchHistoryTrigger?.closePanel();
    this.dialog.open(ProductCodeSearchDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: 'input'
    }).afterClosed().subscribe((id: number | undefined) => {
      if (id) {
        this.router.navigate(['/product', id]);
      }
    });
  }

  /** Открыть клиента, найденного предпоиском. */
  openSearchPreview(): void {
    if (this.searchPreview) {
      this.router.navigateByUrl(this.searchPreview.route);
    }
  }

  /**
   * Убрать привязку к найденной сущности (крестик в бейдже).
   * Запрос в строке остаётся, дальше поиск работает по запчастям.
   */
  dismissSearchPreview(): void {
    this.searchPreview = null;
  }

  onSearchFocus() {
    this.searchHistoryFocus$.next();
  }

  onHistoryOptionSelected() {
    void this.search();
  }

  removeHistoryEntry(entry: SearchHistoryEntry, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.searchHistoryEntries = this.searchHistoryEntries.filter((item) => item.id !== entry.id);
    this.searchHistoryService.remove(entry.id).subscribe({ error: () => undefined });
  }

  getHistoryIcon(kind?: SearchHistoryKind | null) {
    switch (kind) {
      case 'vin':
        return 'directions_car';
      case 'phone':
        return 'person';
      case 'product':
        return 'inventory_2';
      default:
        return 'history';
    }
  }

  trackByHistoryEntry(index: number, entry: SearchHistoryEntry) {
    return entry.id;
  }

  /** Пишем запрос в общую историю поиска (fire-and-forget). */
  private recordSearchHistory(query: string, kind: SearchHistoryKind, label?: string) {
    const trimmed = String(query || '').trim();
    if (trimmed.length < 3) {
      return;
    }
    this.searchHistoryService.record(trimmed, kind, label).subscribe({ error: () => undefined });
  }

  /** Пересчитать предпоиск для текущего значения строки (после смены режима). */
  private refreshSearchPreview(): void {
    this.previewRefreshSubscription?.unsubscribe();
    if (this.searchMode !== 'v2') {
      this.searchPreview = null;
      return;
    }
    this.previewRefreshSubscription = this.resolveSearchPreview(
      String(this.searchControl.value || '')
    ).subscribe((preview) => {
      this.searchPreview = preview;
    });
  }

  /**
   * Распознаёт ввод в режиме v2 и точечно проверяет совпадение:
   * - телефон (10 цифр с ведущим 0, в т.ч. с префиксом 38/+38) → клиент по точному номеру;
   * Возвращает null, если режим не v2, ввод пустой или ничего не найдено.
   */
  private resolveSearchPreview(raw: string): Observable<SearchPreview | null> {
    if (this.searchMode !== 'v2') {
      return of(null);
    }
    const value = raw.trim();
    if (!value) {
      return of(null);
    }

    let digits = value.replace(/\D/g, '');
    if (digits.startsWith('38')) {
      digits = digits.slice(2);
    }

    if (/^0\d{9}$/.test(digits)) {
      return this.clientService.search({ phone: digits }).pipe(
        map((res) => {
          const client = res?.data?.[0];
          if (!client) {
            return null;
          }
          const name = [client.name, client.surname].filter(Boolean).join(' ').trim();
          return {
            type: 'client',
            label: name || 'Клиент',
            hint: client.phone || digits,
            route: `/client/profile/${client.id}`
          } as SearchPreview;
        }),
        catchError(() => of(null))
      );
    }

    return of(null);
  }

  openCurrentMoneyDay(focus?: 'close' | 'reconcile' | 'refunds') {
    this.openManagerMoneyDay(this.selectedManagerMoneyDay, focus);
  }

  openManagerMoneyDay(day?: MoneyDay | null, focus?: 'close' | 'reconcile' | 'refunds') {
    this.dailySummaryTrigger?.closeMenu();
    const targetMoneyDayId =
      Number(day?.id || 0) ||
      Number(this.selectedManagerMoneyDay?.id || 0) ||
      Number(this.currentMoneyDaySummary?.day_id || 0) ||
      Number(this.managerMoneyDaysForPopup?.[0]?.id || 0);

    if (targetMoneyDayId) {
      this.router.navigate([`/docs/money_day/update/${targetMoneyDayId}`], {
        queryParams: focus ? { focus } : {}
      });
      return;
    }

    this.router.navigateByUrl('/docs/money_day/list');
    this.notificationService.notice(
      'Смена откроется автоматически после первого денежного события'
    );
  }

  closeManagerMoneyDay(day?: MoneyDay | null) {
    const dayId = Number(day?.id || 0);
    if (!dayId || this.closingManagerMoneyDayId) return;

    const confirmed = globalThis.confirm(
      `Закрыть смену №${dayId}?\n\nПосле закрытия смена будет передана администратору на инкассацию.`
    );
    if (!confirmed) return;

    const sourceLines = day?.current_expected_lines?.length
      ? day.current_expected_lines
      : day?.lines || [];
    const lines = sourceLines.map((line) => {
      const actualAmount =
        line.money_type === MoneyTypeEnum.nal
          ? this.getMoneyDayCashboxStateAmount(day as MoneyDay)
          : Number(line.expected_amount || 0);
      const payload: {
        money_type: MoneyTypeEnum;
        actual_amount: number;
        actual_payment_amount?: number;
        actual_payment_count?: number;
        actual_refund_amount?: number;
        actual_refund_count?: number;
      } = {
        money_type: line.money_type,
        actual_amount: actualAmount
      };

      if (line.money_type !== MoneyTypeEnum.nal) {
        if (line.expected_payment_amount !== undefined && line.expected_payment_amount !== null) {
          payload.actual_payment_amount = Number(line.expected_payment_amount || 0);
        }
        if (line.expected_payment_count !== undefined && line.expected_payment_count !== null) {
          payload.actual_payment_count = Number(line.expected_payment_count || 0);
        }
        if (line.expected_refund_amount !== undefined && line.expected_refund_amount !== null) {
          payload.actual_refund_amount = Number(line.expected_refund_amount || 0);
        }
        if (line.expected_refund_count !== undefined && line.expected_refund_count !== null) {
          payload.actual_refund_count = Number(line.expected_refund_count || 0);
        }
      }

      return payload;
    });

    this.dailySummaryTrigger?.closeMenu();
    this.closingManagerMoneyDayId = dayId;
    this.moneyDayService.close(dayId, { lines }).subscribe(
      (closedDay) => {
        this.closingManagerMoneyDayId = null;
        this.notificationService.notice(
          `Смена №${closedDay.id || dayId} закрыта и передана администратору на инкассацию`
        );
      },
      (err) => {
        this.closingManagerMoneyDayId = null;
        this.notificationService.warning(extractApiErrorMessage(err, 'Не удалось закрыть смену'));
      }
    );
  }

  openMoneyDayList() {
    this.router.navigateByUrl('/docs/money_day/list');
  }

  openMoneyJournal() {
    this.router.navigate(['/docs/money_event/list'], {
      queryParams: {
        money_day_id: this.currentMoneyDaySummary?.day_id || null,
        business_date: this.currentMoneyDaySummary?.day_id
          ? null
          : this.resolveCurrentBusinessDate(),
        manager_user_id: this.user?.id || null,
        warehouse_id: null
      }
    });
  }

  openCurrentRefundReport() {
    this.openManagerMoneyDay(this.selectedManagerMoneyDay, 'refunds');
  }

  openCurrentCollection() {
    if (!this.currentMoneyDaySummary?.day_id || !this.canCollectCurrentMoneyDay) {
      return;
    }

    this.router.navigate(['/docs/money_collection/create'], {
      queryParams: { money_day_id: this.currentMoneyDaySummary.day_id }
    });
  }

  logout() {
    this.authService.logout();
  }

  async setRingostatOnline(event?: Event) {
    event?.stopPropagation();
    await this.saveRingostatAvailability('online', null);
  }

  async setRingostatDnd(event?: Event) {
    event?.stopPropagation();
    await this.saveRingostatAvailability('dnd', null);
  }

  async setRingostatTemporaryDnd(event: Event | undefined, minutes: number) {
    event?.stopPropagation();
    await this.saveRingostatAvailability('dnd_temporary', minutes);
  }

  isRingostatAvailabilitySelected(status: RingostatAvailabilityStatus) {
    return this.effectiveRingostatAvailabilityStatus === status;
  }

  isRingostatTemporaryMinutesSelected(minutes: number) {
    if (this.effectiveRingostatAvailabilityStatus !== 'dnd_temporary') return false;
    const expiresAt = this.toTime(this.ringostatDndUntil);
    if (!expiresAt) return false;
    const remainingMinutes = Math.ceil((expiresAt - Date.now()) / 60000);
    return remainingMinutes > 0 && Math.abs(remainingMinutes - minutes) <= 2;
  }

  private async loadRingostatAvailability(userId: number) {
    this.ringostatAvailabilityLoading = true;
    try {
      const settings = await firstValueFrom(this.ringostatSettingsService.getMySettings());
      if (settings.userId !== userId) return;
      this.applyRingostatAvailability(settings);
    } catch (error) {
      console.error(error);
      this.resetRingostatAvailability();
    } finally {
      this.ringostatAvailabilityLoading = false;
    }
  }

  private async saveRingostatAvailability(
    availabilityStatus: RingostatAvailabilityStatus,
    dndMinutes: number | null
  ) {
    if (this.ringostatAvailabilitySaving) return;

    this.ringostatAvailabilitySaving = true;
    try {
      const settings = await firstValueFrom(
        this.ringostatSettingsService.saveMySettings({
          availabilityStatus,
          dndMinutes
        })
      );
      this.applyRingostatAvailability(settings);
      this.notificationService.notice(`Статус звонков: ${this.ringostatStatusLabel}`);
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось обновить статус звонков');
    } finally {
      this.ringostatAvailabilitySaving = false;
    }
  }

  private applyRingostatAvailability(settings: RingostatUserSettings) {
    this.ringostatAvailabilityStatus =
      settings.effectiveAvailabilityStatus || settings.availabilityStatus || 'online';
    this.ringostatDndUntil = settings.dndUntil || null;
  }

  private resetRingostatAvailability() {
    this.ringostatAvailabilityStatus = 'online';
    this.ringostatDndUntil = null;
  }

  private isFutureDate(value?: string | Date | null) {
    const time = this.toTime(value);
    return Boolean(time && time > Date.now());
  }

  private toTime(value?: string | Date | null) {
    if (!value) return 0;
    const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(time) ? time : 0;
  }

  private formatRingostatDndUntil(value?: string | Date | null) {
    const time = this.toTime(value);
    if (!time) return '';
    return new Date(time).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getMoneyDayRowStatusLabel(day: MoneyDay) {
    if (day.collection_status === MoneyDayCollectionStatusEnum.COLLECTED) {
      return 'Инкассирована';
    }
    if (
      day.status === MoneyDayStatusEnum.CLOSED &&
      day.collection_status === MoneyDayCollectionStatusEnum.PENDING
    ) {
      return 'Ожидает инкассации';
    }
    if (day.status === MoneyDayStatusEnum.CLOSED) {
      return 'Закрыта';
    }

    return 'Открыта';
  }

  getMoneyDayRowStatusClass(day: MoneyDay) {
    if (day.collection_status === MoneyDayCollectionStatusEnum.COLLECTED) {
      return 'summary-chip summary-chip--success';
    }
    if (
      day.status === MoneyDayStatusEnum.CLOSED &&
      day.collection_status === MoneyDayCollectionStatusEnum.PENDING
    ) {
      return 'summary-chip summary-chip--warning';
    }
    if (day.status === MoneyDayStatusEnum.CLOSED) {
      return 'summary-chip summary-chip--neutral';
    }

    return 'summary-chip summary-chip--info';
  }

  getMoneyDayCashLabel(day: MoneyDay) {
    return day.status === MoneyDayStatusEnum.OPEN ? 'Наличка ожидается' : 'Наличка факт';
  }

  getMoneyDayCashAmount(day: MoneyDay) {
    if (day.cash_summary) {
      return day.status === MoneyDayStatusEnum.OPEN
        ? Number(day.cash_summary.expected_amount || 0)
        : Number(day.cash_summary.actual_amount || 0);
    }
    const cashLine =
      (day.status === MoneyDayStatusEnum.OPEN
        ? (day.current_expected_lines || []).find((line) => line.money_type === MoneyTypeEnum.nal)
        : null) ||
      (day.lines || []).find((line) => line.money_type === MoneyTypeEnum.nal) ||
      (day.current_expected_lines || []).find((line) => line.money_type === MoneyTypeEnum.nal) ||
      null;

    if (!cashLine) {
      return 0;
    }

    return day.status === MoneyDayStatusEnum.OPEN
      ? Number(cashLine.expected_amount || 0)
      : Number(cashLine.actual_amount || 0);
  }

  getMoneyDayCashboxStateAmount(day: MoneyDay) {
    if (day.cash_summary) {
      const ledgerBalance =
        day.cash_summary.current_ledger_balance ?? day.cash_summary.ledger_balance;
      if (ledgerBalance !== undefined && ledgerBalance !== null) {
        return Number(ledgerBalance || 0);
      }
    }
    if (day.cashbox_balance !== undefined && day.cashbox_balance !== null) {
      return Number(day.cashbox_balance || 0);
    }
    return this.getMoneyDayCashAmount(day);
  }

  getMoneyDayExpenseAmount(day: MoneyDay) {
    return this.roundMoney(Math.max(0, Number(day.cash_summary?.expense_amount || 0)));
  }

  getMoneyDayTabLabel(day: MoneyDay) {
    return day.warehouse_name || `Смена #${day.id}`;
  }

  selectManagerMoneyDay(dayId: number) {
    this.selectedManagerMoneyDayId = Number(dayId || 0) || null;
  }

  selectManagerMoneyDaysSummary() {
    this.selectedManagerMoneyDayId = null;
  }

  getMoneyDayTypeRows(day: MoneyDay) {
    const sourceLines =
      day.status === MoneyDayStatusEnum.OPEN &&
      Array.isArray(day.current_expected_lines) &&
      day.current_expected_lines.length
        ? day.current_expected_lines
        : day.lines || day.current_expected_lines || [];

    return this.summaryMoneyTypes.map((moneyType) => {
      const line = (sourceLines || []).find((item) => item.money_type === moneyType);
      const paymentSummary = (day.payment_type_summary || []).find(
        (item) => item.money_type === moneyType
      );
      const value = this.roundMoney(
        paymentSummary
          ? Number(paymentSummary.customer_payments || 0) +
              Number(paymentSummary.correction_reversals || 0) +
              Number(paymentSummary.correction_replacements || 0)
          : moneyType === MoneyTypeEnum.nal && day.cash_summary
          ? Number(day.cash_summary.customer_payments || 0) +
            Number(day.cash_summary.correction_reversals || 0) +
            Number(day.cash_summary.correction_replacements || 0)
          : day.status === MoneyDayStatusEnum.OPEN
          ? Number(line?.expected_amount || 0)
          : Number(line?.actual_amount || 0)
      );

      return {
        moneyType,
        label: moneyType,
        value
      };
    });
  }

  getAmountSignClass(value: number) {
    const normalized = Number(value || 0);
    if (normalized > 0) {
      return 'amount-positive';
    }
    if (normalized < 0) {
      return 'amount-negative';
    }

    return 'amount-neutral';
  }

  getCurrencyRateClass(rate: CurrencyTrendRate) {
    return `currencyRate currencyRate--${rate.trend || 'flat'}`;
  }

  getCurrencyTrendIcon(rate: CurrencyTrendRate) {
    if (rate.trend === 'up') {
      return 'trending_up';
    }
    if (rate.trend === 'down') {
      return 'trending_down';
    }
    return 'trending_flat';
  }

  getCurrencyRateTooltip(rate: CurrencyTrendRate) {
    const previous =
      rate.previousSale === null || rate.previousSale === undefined
        ? 'предыдущего курса нет'
        : `предыдущий ${this.formatCurrencyRate(rate.previousSale)}`;
    const date = this.currencyTrend?.date_created
      ? new Date(this.currencyTrend.date_created).toLocaleString('ru-RU')
      : '';
    return [
      `${rate.currency}: покупка ${this.formatCurrencyRate(
        rate.buy
      )}, продажа ${this.formatCurrencyRate(rate.sale)}`,
      `${this.getCurrencyTrendLabel(rate)}: ${this.formatCurrencyDelta(
        rate.saleDelta
      )}, ${previous}`,
      date ? `Обновлено: ${date}` : ''
    ]
      .filter(Boolean)
      .join('\n');
  }

  getCurrencyTrendLabel(rate: CurrencyTrendRate) {
    if (rate.trend === 'up') {
      return 'Подорожал';
    }
    if (rate.trend === 'down') {
      return 'Подешевел';
    }
    return 'Без изменений';
  }

  getOilRateClass() {
    if (this.oilHistoryError && !this.oilLatest) {
      return 'currencyRate currencyRate--oil currencyRate--down';
    }
    return `currencyRate currencyRate--oil currencyRate--${this.oilLatest?.trend || 'flat'}`;
  }

  getOilDeltaClass() {
    if (this.oilHistoryError && !this.oilLatest) {
      return 'oilHistoryHead__delta oilHistoryHead__delta--down';
    }
    return `oilHistoryHead__delta oilHistoryHead__delta--${this.oilLatest?.trend || 'flat'}`;
  }

  getOilTrendIcon() {
    if (this.oilHistoryError && !this.oilLatest) {
      return 'error_outline';
    }
    if (this.oilLatest?.trend === 'up') {
      return 'trending_up';
    }
    if (this.oilLatest?.trend === 'down') {
      return 'trending_down';
    }
    return 'trending_flat';
  }

  getOilTrendLabel() {
    if (this.oilHistoryError && !this.oilLatest) {
      return 'История Brent не загружена';
    }
    if (this.oilLatest?.trend === 'up') {
      return 'Подорожала за месяц';
    }
    if (this.oilLatest?.trend === 'down') {
      return 'Подешевела за месяц';
    }
    return 'Без изменений за месяц';
  }

  getOilRateTooltip() {
    if (!this.oilLatest) {
      return this.oilHistoryError ? 'Brent: не удалось загрузить историю' : 'Brent: загрузка';
    }

    const monthInfo =
      this.oilLatest.monthPrice === null || this.oilLatest.monthPrice === undefined
        ? 'недостаточно истории за месяц'
        : `месяц назад ${this.formatOilPrice(this.oilLatest.monthPrice)}`;

    return [
      `Brent: ${this.formatOilPrice(this.oilLatest.price)} за баррель`,
      `${this.getOilTrendLabel()}: ${this.formatCurrencyDelta(
        this.oilLatest.monthDelta
      )} (${this.formatOilPercent(this.oilLatest.monthDeltaPercent)}), ${monthInfo}`,
      `Дата цены: ${this.formatOilDate(this.oilLatest.date)}`
    ].join('\n');
  }

  getB2cPresenceClass() {
    if (this.b2cPresenceError && !this.b2cPresence) {
      return 'currencyRate currencyRate--b2c-online currencyRate--down';
    }

    const online = Number(this.b2cPresence?.online || 0);
    return `currencyRate currencyRate--b2c-online currencyRate--${online > 0 ? 'up' : 'flat'}`;
  }

  getB2cPresenceIcon() {
    if (this.b2cPresenceError && !this.b2cPresence) {
      return 'error_outline';
    }
    return Number(this.b2cPresence?.online || 0) > 0 ? 'groups' : 'person_off';
  }

  get b2cPresenceValueLabel() {
    if (this.b2cPresence) {
      return String(Number(this.b2cPresence.online || 0));
    }
    return this.b2cPresenceLoading ? '...' : '!';
  }

  getB2cPresenceTooltip() {
    if (!this.b2cPresence) {
      return this.b2cPresenceError ? 'B2C: не удалось загрузить онлайн' : 'B2C: загрузка онлайна';
    }

    const sampledAt = this.b2cPresence.sampledAt
      ? new Date(this.b2cPresence.sampledAt).toLocaleString('ru-RU')
      : '';
    return [
      `B2C онлайн: ${this.b2cPresence.online}`,
      `Окно активности: ${this.b2cPresence.ttlSeconds} сек.`,
      sampledAt ? `Обновлено: ${sampledAt}` : ''
    ]
      .filter(Boolean)
      .join('\n');
  }

  onOilHistoryMenuOpened() {
    this.renderOilHistoryChartSoon();
  }

  onOilHistoryPeriodClick(event: Event, days: 30 | 90 | 365) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedOilHistoryDays = days;
    this.renderOilHistoryChartSoon();
  }

  keepOilHistoryMenuOpen(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  get selectedOilHistoryPoints() {
    const points = this.oilHistory?.points || [];
    const latestDate = this.oilLatest?.date;
    if (!latestDate) {
      return points;
    }

    const fromDate = this.addDays(latestDate, -this.selectedOilHistoryDays);
    return points.filter((point) => point.date >= fromDate);
  }

  formatCurrencyRate(value: number | null | undefined) {
    const rate = Number(value || 0);
    return rate.toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  formatCurrencyDelta(value: number | null | undefined) {
    const delta = Number(value || 0);
    const prefix = delta > 0 ? '+' : '';
    return `${prefix}${this.formatCurrencyRate(delta)}`;
  }

  formatOilPrice(value: number | null | undefined) {
    return `$${this.formatCurrencyRate(value)}`;
  }

  formatOilPercent(value: number | null | undefined) {
    const percent = Number(value || 0);
    const prefix = percent > 0 ? '+' : '';
    return `${prefix}${this.formatCurrencyRate(percent)}%`;
  }

  formatOilDate(value: string | Date | null | undefined) {
    if (!value) {
      return '';
    }
    return new Date(value).toLocaleDateString('ru-RU');
  }

  uploadFile() {
    const dialogRef = this.dialog.open(UploadComponent);
    dialogRef.componentInstance.doneEvent.subscribe(() => {
      dialogRef.close();
    });
  }

  trackByMoneyDayId(index: number, day: MoneyDay) {
    return Number(day?.id || 0) || index;
  }

  trackByMoneyTypeRow(_index: number, row: HeaderMoneyTypeRow) {
    return row.moneyType;
  }

  private syncSearchControlWithRoute(): void {
    const currentUrl = this.router.url;
    if (currentUrl.startsWith('/laximo/vin/')) {
      const vin = decodeURIComponent(
        currentUrl.slice('/laximo/vin/'.length).split(/[/?#]/)[0] || ''
      );
      if (vin && this.searchControl.value !== vin) {
        this.searchControl.setValue(vin, { emitEvent: false });
      }
      return;
    }
    if (!currentUrl.startsWith('/new-catalog')) {
      return;
    }

    const urlTree = this.router.parseUrl(currentUrl);
    const queryParam = urlTree.queryParams.query;
    const query =
      typeof queryParam === 'string'
        ? queryParam
        : Array.isArray(queryParam) && typeof queryParam[0] === 'string'
        ? queryParam[0]
        : '';

    if (this.searchControl.value !== query) {
      this.searchControl.setValue(query, { emitEvent: false });
    }
  }

  private markMoneyFlowViewStateDirty() {
    this.moneyFlowViewStateDirty = true;
  }

  private ensureMoneyFlowViewState() {
    if (!this.moneyFlowViewStateDirty) {
      return;
    }

    this.rebuildMoneyFlowViewState();
  }

  private rebuildMoneyFlowViewState() {
    const managerMoneyDays = (this._managerMoneyDays || []).filter(
      (day) => !day.manager_user_id || Number(day.manager_user_id) === Number(this.user?.id || 0)
    );
    const uniqueManagerMoneyDays = this.uniqueMoneyDays(managerMoneyDays);
    const openDays = uniqueManagerMoneyDays.filter((day) => day.status === MoneyDayStatusEnum.OPEN);
    const pendingDays = uniqueManagerMoneyDays.filter(
      (day) =>
        day.status === MoneyDayStatusEnum.CLOSED &&
        day.collection_status === MoneyDayCollectionStatusEnum.PENDING
    );
    const hasAmbiguousActiveShift = openDays.length > 1;
    const selectedDay = hasAmbiguousActiveShift ? null : this.resolveSingleOwnCashShift(openDays);
    const popupDays = selectedDay ? [selectedDay] : [];
    const popupVisibleDays = popupDays;
    const isSummarySelected = false;
    const idleCashboxBalance =
      !hasAmbiguousActiveShift && !selectedDay
        ? this.roundMoney(
            Number(
              this._currentMoneyDaySummary?.cash_summary?.current_ledger_balance ??
                this._currentMoneyDaySummary?.cashbox_balance ??
                0
            )
          )
        : 0;
    const summaryStatusLabel = selectedDay
      ? '1 смена'
      : this._currentMoneyDaySummary?.cashbox_id
      ? 'Смена не открыта'
      : this.formatShiftCount(0);
    const summaryTypeRows = selectedDay ? this.getMoneyDayTypeRows(selectedDay) : [];
    const refundReport = this.resolveCurrentShiftRefundReport(
      isSummarySelected,
      popupDays,
      selectedDay
    );

    this.openManagerMoneyDaysCache = openDays;
    this.pendingManagerMoneyDaysCache = pendingDays;
    this.popupVisibleManagerMoneyDaysCache = popupVisibleDays;
    this.managerMoneyDaysForPopupCache = popupDays;
    this.selectedManagerMoneyDayCache = selectedDay;
    this.isManagerMoneyDaysSummarySelectedCache = isSummarySelected;
    this.cashShiftSelectionErrorCache = hasAmbiguousActiveShift
      ? `Найдено несколько открытых смен (${openDays.length}). Обратитесь к администратору.`
      : null;
    this.managerMoneyDaysSummaryStatusLabelCache = summaryStatusLabel;
    this.managerMoneyDaysSummaryCashAmountCache = selectedDay
      ? this.getMoneyDayCashboxStateAmount(selectedDay)
      : idleCashboxBalance;
    this.managerMoneyDaysSummaryTypeRowsCache = summaryTypeRows;
    this.selectedManagerMoneyDayTypeRowsCache = selectedDay
      ? this.getMoneyDayTypeRows(selectedDay).filter(
          (row) => Math.abs(Number(row.value || 0)) > 0.009
        )
      : [];
    this.selectedManagerMoneyDayCashAmountCache = selectedDay
      ? this.getMoneyDayCashboxStateAmount(selectedDay)
      : idleCashboxBalance;
    this.selectedManagerMoneyDayExpenseAmountCache = selectedDay
      ? this.getMoneyDayExpenseAmount(selectedDay)
      : 0;
    this.currentShiftRefundReportCache = refundReport;
    this.returnsByTypeRowsCache = this.buildRefundTypeRows(refundReport);
    this.currentShiftRefundScopeLabelCache = isSummarySelected
      ? summaryStatusLabel
      : selectedDay
      ? this.getMoneyDayTabLabel(selectedDay)
      : this._currentMoneyDaySummary?.cashbox_id
      ? 'Смена не открыта'
      : 'Смена не выбрана';
    this.headerMoneyTotalCache = this.roundMoney(
      selectedDay ? this.getMoneyDayCashboxStateAmount(selectedDay) : idleCashboxBalance
    );
    this.moneyFlowViewStateDirty = false;
  }

  private resolveSingleOwnCashShift(days: MoneyDay[]) {
    const candidates = this.sortMoneyDaysForPopup(days || []);
    if (!candidates.length) return null;

    return (
      candidates.find((day) => day.status === MoneyDayStatusEnum.OPEN) ||
      candidates.find(
        (day) =>
          day.status === MoneyDayStatusEnum.CLOSED &&
          day.collection_status === MoneyDayCollectionStatusEnum.PENDING
      ) ||
      null
    );
  }

  private buildManagerMoneyDaysForPopup(openDays: MoneyDay[], pendingDays: MoneyDay[]) {
    return this.sortMoneyDaysForPopup(this.uniqueMoneyDays([...openDays, ...pendingDays]));
  }

  private resolveSelectedManagerMoneyDay(popupDays: MoneyDay[]) {
    if (!popupDays.length) {
      return null;
    }

    const selectedId = Number(this._selectedManagerMoneyDayId || 0);
    if (selectedId) {
      const selected = popupDays.find((day) => Number(day.id || 0) === selectedId);
      if (selected) {
        return selected;
      }
    }

    return popupDays[0] || null;
  }

  private buildManagerMoneyDaysSummaryTypeRows(days: MoneyDay[]): HeaderMoneyTypeRow[] {
    return this.summaryMoneyTypes.map((moneyType) => ({
      moneyType,
      label: moneyType,
      value: days.reduce((total, day) => {
        const row = this.getMoneyDayTypeRows(day).find((item) => item.moneyType === moneyType);
        return total + Number(row?.value || 0);
      }, 0)
    }));
  }

  private resolveCurrentShiftRefundReport(
    isSummarySelected: boolean,
    popupDays: MoneyDay[],
    selectedDay: MoneyDay | null
  ) {
    if (isSummarySelected) {
      return this.moneyDayRefundReportService.mergeReports(
        popupDays.map((day) => this._shiftRefundReports[Number(day.id || 0)])
      );
    }

    const selectedDayId = Number(selectedDay?.id || 0);
    return (
      (selectedDayId ? this._shiftRefundReports[selectedDayId] : null) ||
      this.createEmptyRefundReport(selectedDayId || null)
    );
  }

  private buildRefundTypeRows(report: MoneyDayRefundReport): HeaderMoneyTypeRow[] {
    return this.summaryMoneyTypes
      .map((moneyType) => ({
        moneyType,
        label: moneyType,
        value: Number(report.by_type?.[moneyType] || 0)
      }))
      .filter((row) => row.value > 0.009);
  }

  private createEmptyRefundReport(moneyDayId: number | null): MoneyDayRefundReport {
    return {
      money_day_id: moneyDayId,
      total: 0,
      by_type: {
        [MoneyTypeEnum.nal]: 0,
        [MoneyTypeEnum.terminal]: 0,
        [MoneyTypeEnum.card]: 0,
        [MoneyTypeEnum.bn]: 0,
        [MoneyTypeEnum.A]: 0
      },
      rows: [],
      total_events: 0,
      has_more: false
    };
  }

  private loadCurrencyTrend(isBackgroundRefresh: boolean) {
    if (!isBackgroundRefresh || !this.currencyTrend) {
      this.currencyTrendLoading = true;
    }

    this.headerCurrencyService.getTrend().subscribe(
      (trend) => {
        this.currencyTrend = trend;
        this.currencyTrendError = null;
        this.currencyTrendLoading = false;
      },
      () => {
        this.currencyTrendError = 'currency_load_error';
        this.currencyTrendLoading = false;
      }
    );
  }

  private loadOilHistory(isBackgroundRefresh: boolean) {
    this.oilHistoryAttempted = true;
    if (!isBackgroundRefresh || !this.oilHistory) {
      this.oilHistoryLoading = true;
    }

    this.headerCurrencyService.getOilHistory(365).subscribe(
      (history) => {
        this.oilHistory = history;
        this.oilHistoryError = null;
        this.oilHistoryLoading = false;
        this.renderOilHistoryChartSoon();
      },
      () => {
        this.oilHistoryError = 'oil_load_error';
        this.oilHistoryLoading = false;
      }
    );
  }

  private loadB2cPresence(isBackgroundRefresh: boolean) {
    if (!isBackgroundRefresh || !this.b2cPresence) {
      this.b2cPresenceLoading = true;
    }

    this.headerCurrencyService.getB2cPresence().subscribe(
      (presence) => {
        this.b2cPresence = presence;
        this.b2cPresenceError = null;
        this.b2cPresenceLoading = false;
      },
      () => {
        this.b2cPresenceError = 'b2c_presence_load_error';
        this.b2cPresenceLoading = false;
      }
    );
  }

  private renderOilHistoryChartSoon() {
    setTimeout(() => {
      void this.renderOilHistoryChart();
    });
  }

  private async renderOilHistoryChart() {
    const canvas = this.oilHistoryChartCanvas?.nativeElement;
    const points = this.selectedOilHistoryPoints;
    if (!canvas || !points.length) {
      return;
    }

    const chartJs = await this.loadChartJs();
    this.destroyOilHistoryChart();
    const labels = points.map((point) => this.formatOilDate(point.date));
    const data = points.map((point) => Number(point.price || 0));
    const config: ChartConfiguration<'line', number[], string> = {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            data,
            borderColor: '#0f766e',
            backgroundColor: 'rgba(20, 184, 166, 0.12)',
            borderWidth: 2,
            fill: true,
            pointRadius: 0,
            tension: 0.32
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => this.formatOilPrice(context.parsed.y)
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              maxTicksLimit: 6
            }
          },
          y: {
            grid: {
              color: 'rgba(148, 163, 184, 0.22)'
            },
            ticks: {
              callback: (value) => this.formatOilPrice(Number(value))
            }
          }
        }
      }
    };

    this.oilHistoryChart = new chartJs.Chart(canvas, config);
  }

  private loadChartJs() {
    if (!this.chartJsLoader) {
      this.chartJsLoader = import('chart.js').then((chartJs) => {
        chartJs.Chart.register(...chartJs.registerables);
        return chartJs;
      });
    }
    return this.chartJsLoader;
  }

  private destroyOilHistoryChart() {
    if (this.oilHistoryChart) {
      this.oilHistoryChart.destroy();
      this.oilHistoryChart = null;
    }
  }

  private restartCurrencyTimer() {
    this.stopCurrencyTimer();
    this.currencyRefreshTimerId = setInterval(() => {
      this.loadCurrencyTrend(true);
      this.loadOilHistory(true);
    }, 15 * 60 * 1000);
  }

  private restartB2cPresenceTimer() {
    this.stopB2cPresenceTimer();
    this.b2cPresenceRefreshTimerId = setInterval(() => {
      this.loadB2cPresence(true);
    }, 30_000);
  }

  private addDays(date: string, days: number) {
    const parsed = new Date(`${date}T00:00:00.000Z`);
    parsed.setUTCDate(parsed.getUTCDate() + days);
    return [
      String(parsed.getUTCFullYear()),
      String(parsed.getUTCMonth() + 1).padStart(2, '0'),
      String(parsed.getUTCDate()).padStart(2, '0')
    ].join('-');
  }

  private stopCurrencyTimer() {
    if (this.currencyRefreshTimerId) {
      clearInterval(this.currencyRefreshTimerId);
      this.currencyRefreshTimerId = null;
    }
  }

  private stopB2cPresenceTimer() {
    if (this.b2cPresenceRefreshTimerId) {
      clearInterval(this.b2cPresenceRefreshTimerId);
      this.b2cPresenceRefreshTimerId = null;
    }
  }

  private loadCurrentMoneyDay(isBackgroundRefresh: boolean) {
    if (!this.user?.id) {
      this.currentMoneyDaySummary = null;
      this.currentMoneyDayLoading = false;
      this.currentMoneyDayError = null;
      return;
    }

    if (!isBackgroundRefresh || !this.currentMoneyDaySummary) {
      this.currentMoneyDayLoading = true;
    }

    this.moneyDayService
      .resolveCurrentMoneyDay(
        this.user.id,
        this.user.defaultWarehouse?.id || 0,
        this.resolveCurrentBusinessDate()
      )
      .subscribe(
        (summary) => {
          this.currentMoneyDaySummary = summary;
          this.currentMoneyDayError = null;
          this.currentMoneyDayLoading = false;
        },
        () => {
          this.currentMoneyDayError = 'current_money_day_load_error';
          this.currentMoneyDayLoading = false;
        }
      );
  }

  private loadManagerMoneyDays(isBackgroundRefresh: boolean) {
    if (!this.user?.id) {
      this.managerMoneyDays = [];
      this.managerMoneyDaysLoading = false;
      this.managerMoneyDaysError = null;
      return;
    }

    if (!isBackgroundRefresh || !this.managerMoneyDays.length) {
      this.managerMoneyDaysLoading = true;
    }

    forkJoin([
      this.moneyDayService.search({
        manager_user_id: this.user.id,
        limit: 20,
        offset: 0
      }),
      this.moneyDayService.search({
        manager_user_id: this.user.id,
        status: MoneyDayStatusEnum.CLOSED,
        collection_status: MoneyDayCollectionStatusEnum.PENDING,
        limit: 100,
        offset: 0
      })
    ]).subscribe(
      ([recentResponse, pendingResponse]) => {
        this.managerMoneyDays = this.uniqueMoneyDays([
          ...(recentResponse.items || []),
          ...(pendingResponse.items || [])
        ]);
        this.managerMoneyDaysError = null;
        this.managerMoneyDaysLoading = false;
        this.loadShiftRefundReports(this.managerMoneyDaysForPopup, isBackgroundRefresh);
      },
      () => {
        this.managerMoneyDaysError = 'manager_money_days_load_error';
        this.managerMoneyDaysLoading = false;
      }
    );
  }

  private refreshMoneyFlowSummary(isBackgroundRefresh: boolean) {
    this.loadCurrentMoneyDay(isBackgroundRefresh);
    this.loadManagerMoneyDays(isBackgroundRefresh);
  }

  private handleCashboxChange(event: MoneyCashboxChangedEvent) {
    const cashboxId = Number(event?.cashbox_id || 0);
    const moneyDayId = Number(event?.money_day_id || 0);
    const managerUserId = Number(event?.manager_user_id || 0);
    const warehouseId = Number(event?.warehouse_id || 0);
    const ledgerEntryId = Number(event?.ledger_entry_id || 0);
    const isLifecycle = event?.change_kind === 'LIFECYCLE';
    if (
      !cashboxId ||
      !moneyDayId ||
      !managerUserId ||
      !warehouseId ||
      (!isLifecycle && !ledgerEntryId)
    ) {
      return;
    }

    const lastLedgerEntryId = Number(this.lastCashboxLedgerEntryIds.get(cashboxId) || 0);
    if (!isLifecycle && ledgerEntryId < lastLedgerEntryId) return;
    if (!isLifecycle && ledgerEntryId) {
      this.lastCashboxLedgerEntryIds.set(cashboxId, ledgerEntryId);
    }

    this.moneyDayService.getOne(moneyDayId).subscribe(
      (day) => {
        if (
          (!isLifecycle &&
            ledgerEntryId &&
            this.lastCashboxLedgerEntryIds.get(cashboxId) !== ledgerEntryId) ||
          (day.manager_user_id && Number(day.manager_user_id) !== managerUserId) ||
          (day.warehouse_id && Number(day.warehouse_id) !== warehouseId) ||
          (day.cashbox_id && Number(day.cashbox_id) !== cashboxId)
        ) {
          return;
        }

        this.managerMoneyDays = this.uniqueMoneyDays([
          day,
          ...this.managerMoneyDays.filter((item) => Number(item.id || 0) !== moneyDayId)
        ]);
        if (day.status === MoneyDayStatusEnum.OPEN) {
          this.currentMoneyDaySummary = this.moneyDayService.toCurrentMoneyDaySummary(
            day,
            day.business_date
          );
          this.loadShiftRefundReports(this.managerMoneyDaysForPopup, true);
          return;
        }

        this.loadCurrentMoneyDay(true);
        this.loadShiftRefundReports([], true);
      },
      () => {
        this.managerMoneyDaysError = 'money_cashbox_changed_load_error';
      }
    );
  }

  private isOwnCashboxChange(event: MoneyCashboxChangedEvent) {
    const isLifecycle = event?.change_kind === 'LIFECYCLE';
    return Boolean(
      event?.cashbox_id &&
        event?.money_day_id &&
        event?.manager_user_id &&
        event?.warehouse_id &&
        (isLifecycle || event?.ledger_entry_id) &&
        Number(event.manager_user_id) === Number(this.user?.id || 0)
    );
  }

  private handleMoneyDayLifecycleChange(day: MoneyDay) {
    if (Number(day?.manager_user_id || 0) !== Number(this.user?.id || 0)) {
      return;
    }

    this.managerMoneyDays = this.uniqueMoneyDays([
      day,
      ...this.managerMoneyDays.filter((item) => Number(item.id || 0) !== Number(day.id || 0))
    ]);

    if (day.status === MoneyDayStatusEnum.OPEN) {
      this.currentMoneyDaySummary = this.moneyDayService.toCurrentMoneyDaySummary(
        day,
        day.business_date
      );
      this.loadShiftRefundReports(this.managerMoneyDaysForPopup, true);
      return;
    }

    this.loadCurrentMoneyDay(true);
    this.loadShiftRefundReports([], true);
  }

  private loadShiftRefundReports(days: MoneyDay[], isBackgroundRefresh: boolean) {
    const ids = this.uniqueMoneyDays(days || [])
      .map((day) => Number(day.id || 0))
      .filter((id) => id > 0);

    if (!ids.length) {
      this.shiftRefundReports = {};
      this.shiftRefundReportsLoading = false;
      this.shiftRefundReportsError = null;
      return;
    }

    const hasMissingReport = ids.some((id) => !this.shiftRefundReports[id]);
    if (!isBackgroundRefresh || hasMissingReport) {
      this.shiftRefundReportsLoading = true;
    }

    forkJoin(ids.map((id) => this.moneyDayRefundReportService.getReport(id))).subscribe(
      (reports) => {
        this.shiftRefundReports = reports.reduce<Record<number, MoneyDayRefundReport>>(
          (acc, report) => {
            if (report.money_day_id) {
              acc[report.money_day_id] = report;
            }
            return acc;
          },
          {}
        );
        this.shiftRefundReportsError = null;
        this.shiftRefundReportsLoading = false;
        this.lastSummaryLoadedAt = new Date();
      },
      () => {
        this.shiftRefundReportsError = 'shift_refunds_load_error';
        this.shiftRefundReportsLoading = false;
      }
    );
  }

  private restartSummaryTimer() {
    this.stopSummaryTimer();
    this.summaryRefreshTimerId = setInterval(() => {
      this.refreshMoneyFlowSummary(true);
    }, 60_000);
  }

  private stopSummaryTimer() {
    if (this.summaryRefreshTimerId) {
      clearInterval(this.summaryRefreshTimerId);
      this.summaryRefreshTimerId = null;
    }
  }

  private resolveCurrentBusinessDate() {
    return (
      this.currentMoneyDaySummary?.business_date || toLocalDateString(new Date()) || new Date()
    );
  }

  private hasPendingManagerCollections() {
    return this.pendingManagerMoneyDays.length > 0;
  }

  private formatShiftCount(count: number) {
    const normalized = Math.max(Number(count || 0), 0);
    const mod10 = normalized % 10;
    const mod100 = normalized % 100;

    if (mod10 === 1 && mod100 !== 11) {
      return `${normalized} смена`;
    }
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
      return `${normalized} смены`;
    }

    return `${normalized} смен`;
  }

  private getHeaderMoneyTotal() {
    this.ensureMoneyFlowViewState();
    return this.headerMoneyTotalCache;
  }

  private sortMoneyDaysForPopup(days: MoneyDay[]) {
    return [...(days || [])].sort((left, right) => {
      const priorityDiff =
        this.getMoneyDayPopupPriority(left) - this.getMoneyDayPopupPriority(right);
      if (priorityDiff !== 0) {
        return priorityDiff;
      }

      return Number(right?.id || 0) - Number(left?.id || 0);
    });
  }

  private getMoneyDayPopupPriority(day: MoneyDay) {
    if (day.status === MoneyDayStatusEnum.OPEN) {
      return 0;
    }
    if (
      day.status === MoneyDayStatusEnum.CLOSED &&
      day.collection_status === MoneyDayCollectionStatusEnum.PENDING
    ) {
      return 1;
    }
    if (
      day.status === MoneyDayStatusEnum.CLOSED &&
      day.collection_status === MoneyDayCollectionStatusEnum.NOT_REQUIRED
    ) {
      return 2;
    }
    if (
      day.status === MoneyDayStatusEnum.CLOSED &&
      day.collection_status === MoneyDayCollectionStatusEnum.COLLECTED
    ) {
      return 3;
    }

    return 4;
  }

  private uniqueMoneyDays(days: MoneyDay[]) {
    const seen = new Set<number>();
    return (days || []).filter((day) => {
      const id = Number(day?.id || 0);
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
  }

  private getMoneyDayTotalAmount(day: MoneyDay) {
    return this.roundMoney(
      this.getMoneyDayTypeRows(day).reduce((sum, row) => sum + Number(row.value || 0), 0)
    );
  }

  private roundMoney(value: number) {
    return Math.round(Number(value || 0) * 100) / 100;
  }
}
